<script lang="ts">
  import type { InventoryItem } from '@clinic/contracts';
  import { Search, X } from '@lucide/svelte';
  import type { Snippet } from 'svelte';
  import { fa } from '../../../shared/i18n/fa';
  import { debounced } from '../../../shared/lib/debounced.svelte';
  import { Button, Input } from '../../../shared/ui';
  import { useInventoryItems } from '../api/inventory-api';
  import { qty } from '../lib/bits';

  /** Search the clinic's active items by name (the server normalizes letters and digits). */
  let {
    orgId,
    value,
    onChange,
    label,
    initialQuery = '',
    autofocus = false,
    more,
  }: {
    orgId: string;
    value: Pick<InventoryItem, 'id' | 'name' | 'unit'> | null;
    onChange: (item: InventoryItem | null) => void;
    label: string;
    /** The search starts from this text. */
    initialQuery?: string;
    autofocus?: boolean;
    /** Rows after the results, given the typed text and the items found. */
    more?: Snippet<[string, InventoryItem[]]>;
  } = $props();

  const id = $props.id();
  // svelte-ignore state_referenced_locally
  let q = $state(initialQuery);
  const search = debounced(() => q.trim());
  // A space in the empty field lists every item (pilot feedback #5).
  const browsing = $derived(q !== '' && q.trim() === '');
  const open = $derived(search.value !== '' || browsing);
  const results = useInventoryItems(
    orgId,
    () => ({ q: search.value }),
    () => open,
  );
  const items = $derived(open ? (results.data?.pages[0]?.items.slice(0, browsing ? 50 : 8) ?? []) : []);
</script>

{#if value}
  <div class="flex flex-col gap-1.5">
    <span class="text-sm font-medium text-text-2">{label}</span>
    <div class="flex min-h-11 items-center justify-between gap-2 rounded-md border border-accent-line bg-accent-soft px-3">
      <span class="truncate font-medium text-accent">{value.name} <span class="text-sm font-normal text-text-3">({value.unit})</span></span>
      <Button variant="ghost" size="sm" onclick={() => onChange(null)} aria-label={`${fa.app.edit}: ${label}`}
        ><X class="size-4" aria-hidden="true" /></Button
      >
    </div>
  </div>
{:else}
  <div class="flex flex-col gap-1.5">
    <label for={id} class="text-sm font-medium text-text-2">{label}</label>
    <div class="relative">
      <Search class="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-text-4" aria-hidden="true" />
      <Input
        {id}
        type="search"
        bind:value={q}
        onkeydown={(e) => {
          if (e.key === 'ArrowDown' && q === '') q = ' ';
        }}
        placeholder={fa.inventory.search}
        class="ps-9"
        {autofocus}
        autocomplete="off"
        aria-controls={`${id}-results`}
      />
    </div>
    {#if !open}<p class="text-xs text-text-3">{fa.app.spaceToBrowse}</p>{/if}
    {#if open}
      <ul id={`${id}-results`} class="max-h-72 overflow-y-auto overflow-x-hidden rounded-md border border-border bg-surface">
        {#each items as item (item.id)}
          <li>
            <button
              type="button"
              class="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-start hover:bg-surface-3"
              onclick={() => {
                q = '';
                onChange(item);
              }}
            >
              <span class="truncate">{item.name}</span>
              <span class="shrink-0 text-sm text-text-3">{qty(item.usableOnHand, item.unit)}</span>
            </button>
          </li>
        {/each}
        {#if more}
          {@render more(q.trim(), items)}
        {:else if !results.isPending && items.length === 0}
          <li class="px-3 py-2 text-sm text-text-3">{browsing ? fa.inventory.empty : fa.inventory.noMatch}</li>
        {/if}
      </ul>
    {/if}
  </div>
{/if}
